import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import ImageUploadField from '../../components/ImageUploadField';
import { Plus, Edit2, Trash2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    instagram: '',
    logo_url: '',
    image_url: '',
    image_media_type: 'image',
    is_active: true
  });

  const fetchSponsors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sponsors`);
      setSponsors(response.data);
    } catch (error) {
      toast.error('Failed to fetch sponsors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSponsors();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openCreateModal = () => {
    setEditingSponsor(null);
    setFormData({
      name: '',
      description: '',
      website: '',
      instagram: '',
      logo_url: '',
      image_url: '',
      image_media_type: 'image',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (sponsor) => {
    setEditingSponsor(sponsor);
    setFormData({
      name: sponsor.name,
      description: sponsor.description || '',
      website: sponsor.website || '',
      instagram: sponsor.instagram || '',
      logo_url: sponsor.logo_url || '',
      image_url: sponsor.image_url || '',
      image_media_type: sponsor.image_media_type || 'image',
      is_active: sponsor.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (editingSponsor) {
        await axios.put(`${API_URL}/api/sponsors/${editingSponsor.id}`, formData, { headers });
        toast.success('Sponsor updated');
      } else {
        await axios.post(`${API_URL}/api/sponsors`, formData, { headers });
        toast.success('Sponsor created');
      }
      setShowModal(false);
      fetchSponsors();
    } catch (error) {
      toast.error('Failed to save sponsor');
    }
  };

  const handleDelete = async (sponsorId) => {
    if (!window.confirm('Are you sure you want to delete this sponsor?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/sponsors/${sponsorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sponsor deleted');
      fetchSponsors();
    } catch (error) {
      toast.error('Failed to delete sponsor');
    }
  };

  return (
    <div data-testid="admin-sponsors">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6]">SPONSORS</h1>
        <button
          onClick={openCreateModal}
          data-testid="add-sponsor-btn"
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Plus size={16} />
          Add Sponsor
        </button>
      </div>

      {loading ? (
        <p className="text-[#A19D98]">Loading...</p>
      ) : sponsors.length === 0 ? (
        <p className="text-[#A19D98]">No sponsors yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Website</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sponsors.map((sponsor) => (
                <tr key={sponsor.id} data-testid={`sponsor-row-${sponsor.id}`}>
                  <td className="text-[#F2EDE6]">{sponsor.name}</td>
                  <td className="text-[#A19D98]">
                    {sponsor.website || sponsor.instagram || '-'}
                  </td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-['Oswald'] tracking-wider ${
                      sponsor.is_active ? 'badge-open' : 'badge-completed'
                    }`}>
                      {sponsor.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(sponsor)}
                        data-testid={`edit-sponsor-${sponsor.id}`}
                        className="p-2 text-[#A19D98] hover:text-[#E8651A]"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(sponsor.id)}
                        data-testid={`delete-sponsor-${sponsor.id}`}
                        className="p-2 text-[#A19D98] hover:text-[#FF3B30]"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <div className="bg-[#141414] border border-[#2A2A2A] p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6]">
                {editingSponsor ? 'EDIT SPONSOR' : 'ADD SPONSOR'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-[#A19D98] hover:text-[#F2EDE6]">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Name *</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="sponsor-name-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Description</label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  data-testid="sponsor-description-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Website</label>
                  <Input
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://..."
                    data-testid="sponsor-website-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Instagram</label>
                  <Input
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    placeholder="https://instagram.com/..."
                    data-testid="sponsor-instagram-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <ImageUploadField
                  value={formData.logo_url}
                  onChange={(url) => setFormData((prev) => ({ ...prev, logo_url: url }))}
                  label="Logo (image only)"
                  testIdPrefix="sponsor-logo"
                  accept="image"
                />
                <ImageUploadField
                  value={formData.image_url}
                  mediaType={formData.image_media_type}
                  onChange={(url, kind) => setFormData((prev) => ({ ...prev, image_url: url, image_media_type: kind || 'image' }))}
                  label="Banner Image or Video"
                  testIdPrefix="sponsor-image"
                  accept="both"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active" className="text-[#A19D98] text-sm">
                  Active (visible on website)
                </label>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="sponsor-submit-btn"
                  className="btn-primary flex-1"
                >
                  {editingSponsor ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSponsors;
